import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGlobalTimers } from '../../context/GlobalTimersContext';
import { useSidebar } from '../../context/SidebarContext';
import UserAvatar from '../common/UserAvatar';
import { Play, Pause, X } from 'lucide-react';
import { Modal } from '../ui/modal';
import { createPortal } from 'react-dom';

/**
 * Widget flotante que muestra timers activos
 * Persiste durante la navegación
 */
export function FloatingTimerWidget() {
  const { timers, getTimerState, pauseTimerById, resumeTimerById, stopTimer, completeTimerById } = useGlobalTimers();
  const { isMobileOpen } = useSidebar();
  const { t } = useTranslation();
  const location = useLocation();
  const [expandedTimerId, setExpandedTimerId] = React.useState<string | null>(null);
  const [terminateConfirmTimerId, setTerminateConfirmTimerId] = React.useState<string | null>(null);
  const [completingById, setCompletingById] = React.useState<Record<string, boolean>>({});
  const [modalTimerId, setModalTimerId] = React.useState<string | null>(null);
  
  // Resetear expansión solo al cambiar de ruta
  React.useEffect(() => {
    setExpandedTimerId(null);
    setModalTimerId(null);
  }, [location.pathname]);

  // ✅ Si un timer se elimina (por ejemplo al cambiar de día y limpiar pausados),
  // cerrar cualquier panel/confirmación que estuviera apuntando a ese timer.
  React.useEffect(() => {
    if (expandedTimerId && !timers.some(t => t.id === expandedTimerId)) {
      setExpandedTimerId(null);
    }
    if (terminateConfirmTimerId && !timers.some(t => t.id === terminateConfirmTimerId)) {
      setTerminateConfirmTimerId(null);
    }
    if (modalTimerId && !timers.some(t => t.id === modalTimerId)) {
      setModalTimerId(null);
    }
  }, [timers, expandedTimerId, terminateConfirmTimerId, modalTimerId]);

  // Mostrar timers activos (corriendo Y pausados)
  // Mantener todos visibles (sin filtro por board), máximo 6
  const activeTimers = React.useMemo(() => {
    return timers.slice(0, 6);
  }, [timers]);

  // Diagnóstico (solo DEV): entender por qué no se renderiza el widget
  React.useEffect(() => {
    try {
      if (!(import.meta as any)?.env?.DEV) return;
      console.log('[FloatingTimerWidget][debug]', {
        timersCount: timers.length,
        activeTimersCount: activeTimers.length,
        firstTimerId: activeTimers[0]?.id,
        firstTimerFamilyId: (activeTimers[0] as any)?.familyId,
        firstTimerMemberId: (activeTimers[0] as any)?.memberId,
      });
    } catch {
    }
  }, [timers.length, activeTimers.length]);

  // Formatear tiempo restante
  const formatTime = (seconds: number) => {
    const absSeconds = Math.abs(seconds);
    const mins = Math.floor(absSeconds / 60);
    const secs = absSeconds % 60;
    const sign = seconds < 0 ? '-' : '';
    return `${sign}${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Color según fase
  const getPhaseColor = (phase: string | null) => {
    const phaseColors: Record<string, string> = {
      green: 'bg-emerald-500',
      amber: 'bg-amber-500',
      red: 'bg-red-500',
      null: 'bg-gray-500',
    };
    return phaseColors[phase || 'null'];
  };

  const expandedTimer = React.useMemo(() => {
    if (!expandedTimerId) return null;
    return activeTimers.find(t => t.id === expandedTimerId) || null;
  }, [activeTimers, expandedTimerId]);

  const modalTimer = React.useMemo(() => {
    if (!modalTimerId) return null;
    return activeTimers.find(t => t.id === modalTimerId) || null;
  }, [activeTimers, modalTimerId]);

  // Mantener el widget visible incluso con el modal abierto
  const widgetZClass = isMobileOpen ? 'z-[50]' : 'z-[55]';
  // Cuando el modal grande está abierto, mantener el widget detrás del modal
  // (el Modal usa z-[99999]).
  const widgetZIndex = modalTimerId ? 99990 : undefined;

  if (activeTimers.length === 0) return null;

  return (
    <>
      {/* Modal grande al tocar una pastilla */}
      {modalTimer && (() => {
        const state = getTimerState(modalTimer);
        const { remainingSec, phase } = state;
        const phaseColor = getPhaseColor(phase);
        const isPaused = !modalTimer.running;
        const completionGoal = modalTimer.completionGoal || 'before_time';
        const borderClass = isPaused
          ? 'border-amber-400 dark:border-amber-500'
          : phase === 'red'
          ? 'border-red-500'
          : 'border-gray-200 dark:border-gray-700';

        return (
          <Modal
            isOpen={true}
            onClose={() => {
              setTerminateConfirmTimerId(null);
              setModalTimerId(null);
            }}
            isFullscreen
            showCloseButton
          >
            <div className="h-full w-full bg-white dark:bg-gray-900">
              <div className={`h-full w-full flex flex-col border-t-4 ${borderClass}`}>
                <div className="px-5 pt-6 pb-4 flex items-center gap-3">
                  <UserAvatar
                    name={modalTimer.memberName}
                    avatarUrl={modalTimer.memberAvatar}
                    colorHex={modalTimer.memberColorHex}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{modalTimer.memberName}</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{modalTimer.activityName}</p>
                  </div>
                </div>

                <div className="flex-1 min-h-0 flex flex-col items-center justify-center px-6">
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-6xl">{modalTimer.activityIcon || '⏱️'}</span>
                    <span
                      className={`font-mono text-6xl font-black tracking-tight ${
                        remainingSec < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {formatTime(remainingSec)}
                    </span>
                  </div>
                  <div className={`mt-6 h-3 w-full max-w-md rounded-full ${phaseColor}`} />

                  {isPaused && (
                    <span className="inline-block mt-4 text-xs bg-amber-500 text-white px-3 py-1 rounded-full font-bold uppercase">
                      {t('timerWidget.paused')}
                    </span>
                  )}
                </div>

                <div className="px-6 pb-8 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    {isPaused ? (
                      <button
                        onClick={() => resumeTimerById(modalTimer.id)}
                        className="col-span-2 bg-emerald-600 hover:bg-emerald-700 text-white text-base font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                      >
                        <Play className="w-5 h-5" />
                        {t('timerWidget.resume')}
                      </button>
                    ) : (
                      <button
                        onClick={() => pauseTimerById(modalTimer.id)}
                        className="col-span-2 bg-amber-500 hover:bg-amber-600 text-white text-base font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                      >
                        <Pause className="w-5 h-5" />
                        {t('timerWidget.pause')}
                      </button>
                    )}

                    {completionGoal === 'before_time' ? (
                      <>
                        <button
                          onClick={async () => {
                            if (completingById[modalTimer.id]) return;
                            setCompletingById((prev) => ({ ...prev, [modalTimer.id]: true }));
                            try {
                              await completeTimerById(modalTimer.id, { reason: 'complete' });
                              setModalTimerId(null);
                            } catch {
                              window.alert(t('timerWidget.errorComplete'));
                            } finally {
                              setCompletingById((prev) => ({ ...prev, [modalTimer.id]: false }));
                            }
                          }}
                          disabled={!!completingById[modalTimer.id]}
                          className={`bg-emerald-700 hover:bg-emerald-800 text-white text-base font-semibold py-4 rounded-xl flex items-center justify-center gap-2 ${
                            completingById[modalTimer.id] ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {completingById[modalTimer.id]
                            ? t('timerWidget.done')
                            : `✅ ${t('timerWidget.done')}`}
                        </button>
                        <button
                          onClick={() => stopTimer(modalTimer.id)}
                          className="bg-red-600 hover:bg-red-700 text-white text-base font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                        >
                          <X className="w-5 h-5" />
                          {t('timerWidget.stop')}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setTerminateConfirmTimerId(modalTimer.id)}
                        className="col-span-2 bg-red-600 hover:bg-red-700 text-white text-base font-semibold py-4 rounded-xl flex items-center justify-center gap-2"
                      >
                        ⛔ {t('timerWidget.terminate')}
                      </button>
                    )}
                  </div>

                  {terminateConfirmTimerId === modalTimer.id && (
                    <div className="mt-4 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                      <p className="text-sm text-red-700 dark:text-red-300 font-semibold">
                        {t('timerWidget.terminateWarning')}
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <button
                          onClick={async () => {
                            if (completingById[modalTimer.id]) return;
                            setCompletingById((prev) => ({ ...prev, [modalTimer.id]: true }));
                            try {
                              await completeTimerById(modalTimer.id, { reason: 'terminate' });
                              setTerminateConfirmTimerId(null);
                              setModalTimerId(null);
                            } catch {
                              window.alert(t('timerWidget.errorTerminate'));
                            } finally {
                              setCompletingById((prev) => ({ ...prev, [modalTimer.id]: false }));
                            }
                          }}
                          disabled={!!completingById[modalTimer.id]}
                          className={`bg-red-700 hover:bg-red-800 text-white text-base font-semibold py-4 rounded-xl ${
                            completingById[modalTimer.id] ? 'opacity-70 cursor-not-allowed' : ''
                          }`}
                        >
                          {completingById[modalTimer.id]
                            ? t('timerWidget.terminateAnyway')
                            : t('timerWidget.terminateAnyway')}
                        </button>
                        <button
                          onClick={() => setTerminateConfirmTimerId(null)}
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 text-base font-semibold py-4 rounded-xl"
                        >
                          {t('timerWidget.continue')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Pastillas: siempre igual, sin deformarse (portal al body para evitar stacking issues con el Modal) */}
      {typeof document !== 'undefined' && createPortal(
        <div
          className={`fixed right-4 left-4 md:left-auto ${widgetZClass} pointer-events-auto`}
          style={{
            bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
            zIndex: widgetZIndex,
          }}
        >
          <div className="flex flex-wrap gap-2 justify-end max-w-full">
            {activeTimers.map((timer) => {
              const state = getTimerState(timer);
              const { remainingSec, phase } = state;
              const phaseColor = getPhaseColor(phase);
              const isPaused = !timer.running;
              const borderClass = isPaused
                ? 'border-amber-400 dark:border-amber-500'
                : phase === 'red'
                ? 'border-red-500'
                : 'border-gray-200 dark:border-gray-700';

              return (
                <div
                  key={timer.id}
                  className={`bg-white dark:bg-gray-800 shadow-lg border-2 ${borderClass} rounded-full ${
                    isPaused ? 'opacity-90' : ''
                  }`}
                >
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      setTerminateConfirmTimerId(null);
                      setModalTimerId(timer.id);
                    }}
                    className="px-3 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-full transition-colors"
                  >
                    <UserAvatar
                      name={timer.memberName}
                      avatarUrl={timer.memberAvatar}
                      colorHex={timer.memberColorHex}
                      size="sm"
                    />
                    <span className="text-xl">{timer.activityIcon || '⏱️'}</span>
                    <span
                      className={`font-mono text-base font-bold ${
                        remainingSec < 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {formatTime(remainingSec)}
                    </span>
                    {isPaused && <Pause className="w-4 h-4 text-amber-500" />}
                    <div className={`w-2 h-2 rounded-full ${phaseColor}`} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

